import {
  getCriticalIncidents,
  getDiagnosticsByAttemptId,
  getDiagnosticsByIntentId,
  getDiagnosticsByOrderId,
  getWebhookRequests,
  resolveIncidents,
} from 'http/paymentDiagnosticsApi';

import {
  ICriticalIncident,
  IPaymentDiagnostics,
  IPaymentWebhookRequest,
  TPaymentLookupType,
} from 'common/types/payment-diagnostics';
import { notify } from 'common/utils';
import { makeAutoObservable } from 'mobx';

export const PAYMENT_LOGS_PAGE_SIZE = 50;

const lookupTitles: Record<TPaymentLookupType, string> = {
  order: 'Order',
  attempt: 'Attempt',
  intent: 'Intent',
};

export class PaymentLogsViewModel {
  _incidents: ICriticalIncident[] = [];
  _incidentsLoading = false;
  _incidentsPage = 1;
  _incidentsCount = 0;
  _incidentType: string | undefined = undefined;
  _incidentProvider: string | undefined = undefined;

  _webhookRequests: IPaymentWebhookRequest[] = [];
  _webhookRequestsLoading = false;
  _webhookRequestsPage = 1;
  _webhookRequestsCount = 0;
  _webhookOutcome: string | undefined = undefined;
  _webhookIntentId = '';

  _detail: IPaymentDiagnostics | null = null;
  _detailLoading = false;
  _detailTitle = '';
  _isDetailOpen = false;
  // Kept so the drawer can be refreshed in place after closing an incident.
  _detailType: TPaymentLookupType = 'order';
  _detailValue = '';

  _lookupType: TPaymentLookupType = 'order';
  _lookupQuery = '';

  _selectedIncidentIds: number[] = [];
  _resolveIds: number[] = [];
  _resolveReason = '';
  _isResolveOpen = false;
  _resolveLoading = false;

  get incidents() {
    return this._incidents;
  }

  get incidentsLoading() {
    return this._incidentsLoading;
  }

  get incidentsPage() {
    return this._incidentsPage;
  }

  get incidentsCount() {
    return this._incidentsCount;
  }

  get incidentType() {
    return this._incidentType;
  }

  get incidentProvider() {
    return this._incidentProvider;
  }

  get webhookRequests() {
    return this._webhookRequests;
  }

  get webhookRequestsLoading() {
    return this._webhookRequestsLoading;
  }

  get webhookRequestsPage() {
    return this._webhookRequestsPage;
  }

  get webhookRequestsCount() {
    return this._webhookRequestsCount;
  }

  get webhookOutcome() {
    return this._webhookOutcome;
  }

  get webhookIntentId() {
    return this._webhookIntentId;
  }

  get detail() {
    return this._detail;
  }

  get detailLoading() {
    return this._detailLoading;
  }

  get detailTitle() {
    return this._detailTitle;
  }

  get isDetailOpen() {
    return this._isDetailOpen;
  }

  get lookupType() {
    return this._lookupType;
  }

  get lookupQuery() {
    return this._lookupQuery;
  }

  get selectedIncidentIds() {
    return this._selectedIncidentIds;
  }

  get resolveIds() {
    return this._resolveIds;
  }

  get resolveReason() {
    return this._resolveReason;
  }

  get isResolveOpen() {
    return this._isResolveOpen;
  }

  get resolveLoading() {
    return this._resolveLoading;
  }

  /** Incidents of the currently open attempt that can still be closed. */
  get openDetailIncidentIds() {
    return (this._detail?.incidents ?? [])
      .filter((incident) => incident.status === 'OPEN')
      .map((incident) => incident.id);
  }

  setLookupType = (type: TPaymentLookupType) => {
    this._lookupType = type;
    this._lookupQuery = '';
  };

  setLookupQuery = (query: string) => {
    this._lookupQuery = query;
  };

  closeDetail = () => {
    this._isDetailOpen = false;
  };

  setSelectedIncidentIds = (ids: number[]) => {
    this._selectedIncidentIds = ids;
  };

  openResolveModal = (ids: number[]) => {
    if (!ids.length) {
      return;
    }

    this._resolveIds = ids;
    this._resolveReason = '';
    this._isResolveOpen = true;
  };

  closeResolveModal = () => {
    this._isResolveOpen = false;
  };

  setResolveReason = (reason: string) => {
    this._resolveReason = reason;
  };

  confirmResolve = async () => {
    this._resolveLoading = true;

    try {
      const { resolved, skipped } = await resolveIncidents(
        this._resolveIds,
        this._resolveReason,
      );

      notify(
        skipped.length
          ? `Closed ${resolved}, skipped ${skipped.length} already closed`
          : `Closed ${resolved} incident${resolved === 1 ? '' : 's'}`,
        'success',
      );

      this._isResolveOpen = false;
      this._selectedIncidentIds = [];

      await this.fetchIncidents();

      // The drawer may be showing one of the incidents that was just closed.
      if (this._isDetailOpen && this._detailValue) {
        await this.openDiagnostics(this._detailType, this._detailValue);
      }
    } catch (e) {
      notify(`Error, reason:${e}`, 'error');
    } finally {
      this._resolveLoading = false;
    }
  };

  fetchIncidents = async () => {
    this._incidentsLoading = true;

    try {
      const { count, items } = await getCriticalIncidents({
        page: this._incidentsPage,
        limit: PAYMENT_LOGS_PAGE_SIZE,
        type: this._incidentType,
        provider: this._incidentProvider,
      });

      this._incidents = items;
      this._incidentsCount = count;
      // Drop selections that are no longer on the page.
      const visibleIds = items.map((incident) => incident.incidentId);
      this._selectedIncidentIds = this._selectedIncidentIds.filter((id) =>
        visibleIds.includes(id),
      );
    } catch (e) {
      notify(`Error, reason:${e}`, 'error');
    } finally {
      this._incidentsLoading = false;
    }
  };

  changeIncidentsPage = (page: number) => {
    this._incidentsPage = page;

    this.fetchIncidents();
  };

  changeIncidentType = (type?: string) => {
    this._incidentType = type;
    this._incidentsPage = 1;

    this.fetchIncidents();
  };

  changeIncidentProvider = (provider?: string) => {
    this._incidentProvider = provider;
    this._incidentsPage = 1;

    this.fetchIncidents();
  };

  fetchWebhookRequests = async () => {
    this._webhookRequestsLoading = true;

    try {
      const { count, items } = await getWebhookRequests({
        page: this._webhookRequestsPage,
        limit: PAYMENT_LOGS_PAGE_SIZE,
        outcome: this._webhookOutcome,
        providerIntentId: this._webhookIntentId.trim() || undefined,
      });

      this._webhookRequests = items;
      this._webhookRequestsCount = count;
    } catch (e) {
      notify(`Error, reason:${e}`, 'error');
    } finally {
      this._webhookRequestsLoading = false;
    }
  };

  changeWebhookRequestsPage = (page: number) => {
    this._webhookRequestsPage = page;

    this.fetchWebhookRequests();
  };

  changeWebhookOutcome = (outcome?: string) => {
    this._webhookOutcome = outcome;
    this._webhookRequestsPage = 1;

    this.fetchWebhookRequests();
  };

  changeWebhookIntentId = (intentId: string) => {
    this._webhookIntentId = intentId;
  };

  searchWebhookRequests = () => {
    this._webhookRequestsPage = 1;

    this.fetchWebhookRequests();
  };

  openDiagnostics = async (type: TPaymentLookupType, value: string) => {
    const query = value.trim();

    if (!query) {
      return;
    }

    if (type !== 'intent' && !/^\d+$/.test(query)) {
      notify(`${lookupTitles[type]} ID must be a number`, 'warning');

      return;
    }

    this._isDetailOpen = true;
    this._detailLoading = true;
    this._detail = null;
    this._detailTitle = `${lookupTitles[type]} ${query}`;
    this._detailType = type;
    this._detailValue = query;

    try {
      if (type === 'intent') {
        this._detail = await getDiagnosticsByIntentId(query);
      } else if (type === 'attempt') {
        this._detail = await getDiagnosticsByAttemptId(Number(query));
      } else {
        this._detail = await getDiagnosticsByOrderId(Number(query));
      }
    } catch (e) {
      notify(`Error, reason:${e}`, 'error');
      this._isDetailOpen = false;
    } finally {
      this._detailLoading = false;
    }
  };

  submitLookup = () => {
    this.openDiagnostics(this._lookupType, this._lookupQuery);
  };

  constructor() {
    makeAutoObservable(this);
  }
}
